import React, { useState, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CompetitorMonitoring = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, compRes] = await Promise.all([
        fetch(`${API_URL}/api/competitors/dashboard`),
        fetch(`${API_URL}/api/competitors/compare`)
      ]);
      if (dashRes.ok) setDashboard(await dashRes.json());
      if (compRes.ok) setCompetitors((await compRes.json()).comparison || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tabs = [
    { id: 'dashboard', label: 'Overview', icon: '📊' },
    { id: 'compare', label: 'Compare', icon: '📊' },
    { id: 'keywords', label: 'Keywords', icon: '🔍' },
    { id: 'alerts', label: 'Alerts', icon: '🔔' },
  ];

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div></div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet><title>Competitor Monitoring | Admin</title></Helmet>

      <div className="bg-gradient-to-r from-red-600 to-pink-600 text-white px-6 py-4">
        <h1 className="text-2xl font-bold">🕵️ Competitor Monitoring</h1>
        <p className="text-red-100 text-sm">Track competitor rankings, reviews, and GBP activity</p>
      </div>

      <div className="border-b border-gray-200 px-6 bg-white">
        <nav className="flex space-x-6">
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              className={`py-4 px-2 border-b-2 font-medium text-sm transition ${activeTab === tab.id ? 'border-red-500 text-red-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {tab.icon} {tab.label}
            </button>
          ))}
        </nav>
      </div>

      <div className="p-6">
        {activeTab === 'dashboard' && dashboard && (
          <div>
            {/* Our Stats vs Competitors */}
            <div className="bg-gradient-to-r from-teal-50 to-green-50 border border-teal-200 rounded-lg p-6 mb-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="text-3xl">🏆</div>
                <div>
                  <h2 className="text-xl font-bold text-teal-800">{dashboard.our_stats.name}</h2>
                  <p className="text-teal-600">Market Leader in Fort Worth</p>
                </div>
              </div>
              <div className="grid md:grid-cols-4 gap-4">
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-yellow-500">★ {dashboard.our_stats.google_rating}</p>
                  <p className="text-sm text-gray-500">Google Rating</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-teal-600">{dashboard.our_stats.google_reviews}</p>
                  <p className="text-sm text-gray-500">Total Reviews</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-green-600">+{dashboard.competitive_position.rating_vs_avg}</p>
                  <p className="text-sm text-gray-500">vs Avg Rating</p>
                </div>
                <div className="bg-white rounded-lg p-4 text-center">
                  <p className="text-3xl font-bold text-blue-600">+{dashboard.competitive_position.reviews_vs_avg}</p>
                  <p className="text-sm text-gray-500">vs Avg Reviews</p>
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                {dashboard.our_stats.awards?.map((award, i) => (
                  <span key={i} className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm">🏆 {award}</span>
                ))}
              </div>
            </div>

            {/* Competitors Grid */}
            <h3 className="font-semibold text-gray-800 mb-4">Competitors ({dashboard.summary.total_competitors})</h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {dashboard.competitors?.map(c => (
                <div key={c.name} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start mb-2">
                    <h4 className="font-semibold text-gray-800">{c.name}</h4>
                    {c.distance_miles && <span className="text-gray-500 text-sm">{c.distance_miles} mi</span>}
                  </div>
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-yellow-500 font-bold">★ {c.google_rating || 'N/A'}</span>
                    <span className="text-gray-500 text-sm">({c.google_reviews || 0} reviews)</span>
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {c.services?.slice(0, 3).map((s, i) => (
                      <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">{s}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {activeTab === 'compare' && (
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Practice</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Rating</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Reviews</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Key Differentiators</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {competitors.map(c => (
                  <tr key={c.name} className={c.is_us ? 'bg-teal-50' : 'hover:bg-gray-50'}>
                    <td className="px-4 py-3">
                      <span className={`font-medium ${c.is_us ? 'text-teal-700' : 'text-gray-800'}`}>
                        {c.is_us && '🏆 '}{c.name}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-yellow-500 font-bold">★ {c.google_rating || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3 font-medium">{c.google_reviews || 0}</td>
                    <td className="px-4 py-3">
                      {c.key_differentiators ? (
                        <div className="flex flex-wrap gap-1">
                          {c.key_differentiators.slice(0, 3).map((d, i) => (
                            <span key={i} className="bg-teal-100 text-teal-700 text-xs px-2 py-0.5 rounded">{d}</span>
                          ))}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {c.services?.slice(0, 3).map((s, i) => (
                            <span key={i} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded">{s}</span>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {activeTab === 'keywords' && dashboard && (
          <div>
            <div className="bg-white rounded-lg shadow mb-6 p-4">
              <h3 className="font-semibold mb-2">Target Keywords</h3>
              <p className="text-sm text-gray-600">Track rankings for these high-value local keywords</p>
            </div>
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Keyword</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Search Volume</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {dashboard.target_keywords?.map(kw => (
                    <tr key={kw.keyword} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{kw.keyword}</td>
                      <td className="px-4 py-3 text-gray-600">{kw.search_volume}/mo</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          kw.priority >= 5 ? 'bg-red-100 text-red-700' :
                          kw.priority >= 4 ? 'bg-orange-100 text-orange-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {kw.priority >= 5 ? 'Critical' : kw.priority >= 4 ? 'High' : 'Medium'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'alerts' && (
          <div>
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="font-semibold text-gray-800 mb-4">🔔 Competitor Alerts</h3>
              {dashboard?.active_alerts?.length > 0 ? (
                <div className="divide-y">
                  {dashboard.active_alerts.map(a => (
                    <div key={a.id} className="py-4">
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs ${
                          a.severity === 'critical' ? 'bg-red-100 text-red-700' :
                          a.severity === 'warning' ? 'bg-yellow-100 text-yellow-700' : 'bg-blue-100 text-blue-700'
                        }`}>{a.severity}</span>
                        <span className="font-medium">{a.competitor_name}</span>
                      </div>
                      <p className="text-gray-600 text-sm mt-1">{a.description}</p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500">No active alerts. We'll notify you when competitors make significant changes.</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompetitorMonitoring;
